import type { ReactNode } from "react";
import {
  Calendar,
  Car,
  KeyRound,
  Mail,
  MapPin,
  Phone,
  Plug,
  Receipt,
  Radio,
  Tag,
  User,
  Users,
  Zap,
} from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { BookingCompanionsPanel } from "@/components/bookings/BookingCompanionsPanel";
import { StayCalendarNotes } from "@/components/bookings/StayCalendarNotes";
import { CreateBookingInvoiceModal } from "@/components/bookings/CreateBookingInvoiceModal";
import { GuestStayInvoicesSection } from "@/components/bookings/GuestStayInvoicesSection";
import { PitchPlanBadge } from "@/components/bookings/PitchPlanBadge";
import { useGuestStayInvoiceState } from "@/components/bookings/useGuestStayInvoiceState";
import { OpenedInvoiceDraftModal } from "@/components/invoices/OpenedInvoiceDraftModal";
import { ComfortCheckInMark } from "@/components/bookings/ComfortCheckInMark";
import { formatDateTimeShort, formatIsoDateShort } from "@/lib/bookingDates";
import { pitchCategoryName } from "@/lib/bookingPitchDisplay";
import { guestTypeLabel } from "@/lib/guestType";
import {
  formatBookingAddons,
  formatBookingSource,
  formatGuestFieldValue,
  formatWantsElectricity,
  displayBookingNotes,
  vehicleFieldValue,
} from "@/lib/inquiryDisplay";
import { pitchAssignmentLabel } from "@/lib/pitchAssignment";
import { formatCurrency, formatStayAmount } from "@/lib/money";
import { useArrivalTypes } from "@/lib/useArrivalTypes";
import { usePitchCategories } from "@/lib/usePitchCategories";
import { featureNameById, usePitchFeatures } from "@/lib/usePitchFeatures";
import { formatLicensePlate, vehicleTypeLabel } from "@/lib/vehicle";
import { VEHICLE_FIELD_ROWS } from "@/lib/vehicleFields";
import {
  bookingDraftInvoiceAmount,
  hasBookingDraftInvoice,
} from "@/lib/bookingInvoiceStatus";
import { hasMissingInvoice, hasOpenInvoiceAmount } from "@/lib/bookingInvoiceDisplay";
import { hasNotInvoicedStayAmount, stayPaymentGap } from "@/lib/stayPaymentGap";
import { PlaceBanBadge, PlaceBanNotice } from "@/components/bookings/PlaceBanNotice";
import type { Booking, BookingDeparture, Guest, Pitch } from "@/types/camping";

function hasOpenInvoice(booking: Booking | BookingDeparture): booking is BookingDeparture {
  return "open_invoice_amount" in booking;
}

export interface GuestStayModalProps {
  open: boolean;
  booking: Booking | BookingDeparture | null;
  guest: Guest | null;
  pitch: Pitch | null;
  /** Offener Rechnungsbetrag (z. B. aus Dashboard-KPI-Daten). */
  openInvoice?: { amount: string; currency: string } | null;
  canEdit: boolean;
  checkoutStrict?: boolean;
  checkOutLoading?: boolean;
  onClose: () => void;
  onStartCheckIn?: () => void;
  onCheckOut?: () => void;
  onConfirm?: () => void;
  onReject?: () => void;
  onEditBooking?: () => void;
  onEditGuest?: () => void;
  onOpenInvoice?: () => void;
  /** Rechnungsentwurf nach abgebrochenem Check-in fortsetzen. */
  onResumeDraftInvoice?: () => void;
  /** Aktiviert Rechnung fortsetzen/erstellen inkl. Modals. */
  userGroups?: string[];
  onInvoiceChanged?: () => void;
}

function InfoItem({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: ReactNode;
}) {
  if (!value || value === "—") return null;
  return (
    <div className="flex gap-3">
      <span className="mt-0.5 shrink-0 text-label-tertiary">{icon}</span>
      <div className="min-w-0">
        <p className="text-xs font-medium uppercase tracking-wide text-label-tertiary">{label}</p>
        <p className="mt-0.5 text-sm text-label">{value}</p>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-label-secondary">{title}</h3>
      <div className="space-y-3 rounded-xl border border-separator bg-fill-tertiary/30 p-4">
        {children}
      </div>
    </section>
  );
}

export function GuestStayModal({
  open,
  booking,
  guest,
  pitch,
  openInvoice,
  canEdit,
  checkoutStrict = false,
  checkOutLoading = false,
  onClose,
  onStartCheckIn,
  onCheckOut,
  onConfirm,
  onReject,
  onEditBooking,
  onEditGuest,
  onOpenInvoice,
  onResumeDraftInvoice,
  userGroups,
  onInvoiceChanged,
}: GuestStayModalProps) {
  const invoiceState = useGuestStayInvoiceState({ booking, open, userGroups });
  const {
    canInvoice,
    canCreateInvoice,
    invoices,
    invoicesReady,
    hasNoInvoice: fetchedHasNoInvoice,
    resumeDraftInvoice,
    openCreateInvoice,
    openedInvoiceId,
    setOpenedInvoiceId,
    createInvoiceOpen,
    setCreateInvoiceOpen,
    handleInvoiceCreated,
    reloadInvoices,
  } = invoiceState;
  const { labelFor: arrivalTypeLabel } = useArrivalTypes();
  const { categories } = usePitchCategories(open);
  const { features: pitchFeatures } = usePitchFeatures(open);

  if (!booking) return null;

  const guestDisplayName = guest
    ? `${guest.last_name}, ${guest.first_name}`
    : booking.guest_name ?? "Unbekannter Gast";

  const pitchLabel = pitch
    ? `${pitch.code}${pitch.label ? ` · ${pitch.label}` : ""}`
    : booking.pitch_id
      ? booking.pitch_id.slice(0, 8)
      : pitchAssignmentLabel(booking.pitch_assignment_mode);

  const partyParts: string[] = [];
  const adults = booking.adults ?? 2;
  const children = booking.children ?? 0;
  const dogs = booking.dogs ?? 0;
  partyParts.push(`${adults} Erw.`);
  if (children > 0) partyParts.push(`${children} Kind.`);
  if (dogs > 0) partyParts.push(`${dogs} Hund${dogs > 1 ? "e" : ""}`);

  const invoiceFromDeparture = hasOpenInvoice(booking)
    ? { amount: booking.open_invoice_amount, currency: booking.currency }
    : null;
  const invoice = openInvoice ?? invoiceFromDeparture;
  const openAmount = invoice ? Number(invoice.amount) : 0;
  const hasUnpaid = hasOpenInvoiceAmount(booking) || openAmount > 0;
  const hasDraftInvoice = hasBookingDraftInvoice(booking);
  const draftAmount = bookingDraftInvoiceAmount(booking);
  const hasNoInvoice =
    (hasMissingInvoice(booking) && !hasUnpaid) || (fetchedHasNoInvoice && !hasUnpaid);
  const paymentGap = invoicesReady ? stayPaymentGap(booking, invoices) : null;
  const notInvoiced = hasNotInvoicedStayAmount(paymentGap);
  const showResumeDraft =
    hasDraftInvoice &&
    (onResumeDraftInvoice || (Boolean(userGroups) && canInvoice));
  const handleResumeDraft = onResumeDraftInvoice ?? resumeDraftInvoice;

  const canCheckIn =
    canEdit &&
    onStartCheckIn &&
    (booking.status === "confirmed" ||
      booking.status === "reserved" ||
      booking.status === "requested");

  const canConfirm = canEdit && onConfirm && booking.status === "requested";
  const canReject = canEdit && onReject && booking.status === "requested";
  const placeBanFlagged = "place_ban_flagged" in booking && booking.place_ban_flagged;

  const canCheckOut =
    canEdit &&
    onCheckOut &&
    booking.status === "checked_in" &&
    !(checkoutStrict && hasUnpaid);

  const licensePlate = [booking.license_plate, booking.license_plate_country]
    .filter(Boolean)
    .join(" · ");

  const guestAddress = guest
    ? [guest.street, [guest.postal_code, guest.city].filter(Boolean).join(" "), guest.country_code]
        .filter(Boolean)
        .join(", ")
    : null;

  const categoryLabel = pitchCategoryName(booking.pitch_category_id, categories);
  const arrivalType = arrivalTypeLabel(booking.arrival_type);
  const electricityLabel = (() => {
    const base = formatWantsElectricity(booking.wants_electricity);
    if (!base) return null;
    if (booking.wants_electricity && booking.electricity_kwh != null) {
      return `${base} (${booking.electricity_kwh} kWh)`;
    }
    return base;
  })();
  const vehicleType =
    booking.vehicle_type && booking.vehicle_type !== booking.arrival_type
      ? vehicleTypeLabel(booking.vehicle_type)
      : null;
  const vehicleRows = VEHICLE_FIELD_ROWS.map((field) => ({
    label: field.label,
    value: vehicleFieldValue(booking as Booking, field.key),
  })).filter((row) => row.value);
  const addonLines = formatBookingAddons(booking.online_booking_addons);
  const requestedFeatureNames = (booking.requested_feature_ids ?? [])
    .map((id) => featureNameById(pitchFeatures).get(id))
    .filter((name): name is string => Boolean(name));
  const bookingSource = formatBookingSource(booking.source);
  const bookingMessage = displayBookingNotes(booking.notes);
  const guestBirthdate = guest?.date_of_birth
    ? formatGuestFieldValue("date_of_birth", guest.date_of_birth)
    : null;

  const footer = (
    <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
      {canConfirm ? (
        <Button type="button" onClick={onConfirm}>
          {placeBanFlagged ? "Trotz Platzverbot bestätigen" : "Anfrage bestätigen"}
        </Button>
      ) : null}
      {canReject ? (
        <Button type="button" variant="danger" onClick={onReject}>
          Anfrage ablehnen
        </Button>
      ) : null}
      {canCheckIn ? (
        <Button type="button" onClick={onStartCheckIn}>
          Check-in
        </Button>
      ) : null}
      {canCheckOut ? (
        <Button type="button" loading={checkOutLoading} onClick={onCheckOut}>
          Check-out
        </Button>
      ) : null}
      {hasUnpaid && onOpenInvoice ? (
        <Button type="button" variant="secondary" onClick={onOpenInvoice}>
          Rechnung öffnen
        </Button>
      ) : null}
      {showResumeDraft ? (
        <Button type="button" variant="secondary" onClick={handleResumeDraft}>
          Rechnung fortsetzen
        </Button>
      ) : null}
      {canCreateInvoice ? (
        <Button type="button" variant="secondary" onClick={openCreateInvoice}>
          Rechnung erstellen
        </Button>
      ) : null}
      {onEditBooking ? (
        <Button type="button" variant="secondary" onClick={onEditBooking}>
          Buchung bearbeiten
        </Button>
      ) : null}
      {onEditGuest && guest ? (
        <Button type="button" variant="ghost" onClick={onEditGuest}>
          Gast bearbeiten
        </Button>
      ) : null}
      <Button type="button" variant="ghost" className="sm:ml-auto" onClick={onClose}>
        Schließen
      </Button>
    </div>
  );

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title={guestDisplayName}
        description={`Buchung ${booking.booking_number} · ${formatIsoDateShort(booking.arrival_date)} → ${formatIsoDateShort(booking.departure_date)}`}
        size="xl"
        footer={footer}
      >
      <div className="space-y-5">
        {placeBanFlagged ? <PlaceBanNotice booking={booking as Booking} /> : null}

        {booking.status === "requested" && canEdit && (canConfirm || canReject) ? (
          <p className="rounded-xl border border-status-orange/40 bg-status-orange/10 px-4 py-3 text-sm text-label">
            Offene Online-Anfrage – prüfen Sie Gastprofil und Aufenthaltshistorie. Mit „Anfrage
            bestätigen“ oder „Anfrage ablehnen“ öffnen Sie die E-Mail-Antwort.
          </p>
        ) : null}

        {booking.comfort_check_in_completed ? (
          <div
            className="rounded-xl border border-status-green/40 bg-status-green/10 px-4 py-3 text-sm text-label"
            role="status"
          >
            <p className="font-semibold text-status-green">Comfort Check-in abgeschlossen</p>
            <p className="mt-1">
              {booking.comfort_check_in_completed_at
                ? `Abgeschlossen am ${formatDateTimeShort(booking.comfort_check_in_completed_at)}. `
                : ""}
              {booking.license_plate
                ? `Kennzeichen ${formatLicensePlate(booking.license_plate, booking.license_plate_country)}. `
                : ""}
              Die Rechnung liegt bei den Dokumenten dieser Buchung.
            </p>
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={booking.status} />
          <ComfortCheckInMark
            completed={booking.comfort_check_in_completed}
            paymentStatus={booking.comfort_check_in_payment_status}
          />
          {placeBanFlagged ? <PlaceBanBadge /> : null}
          {booking.source === "online" ? (
            <span className="rounded-full bg-accent-soft px-2.5 py-0.5 text-xs font-medium text-accent">
              Online
            </span>
          ) : null}
          {hasUnpaid ? (
            <span className="rounded-full bg-status-orange/15 px-2.5 py-0.5 text-xs font-semibold text-status-orange">
              Offen {formatCurrency(invoice!.amount, invoice!.currency)}
            </span>
          ) : null}
          {notInvoiced && paymentGap ? (
            <span className="rounded-full bg-status-orange/15 px-2.5 py-0.5 text-xs font-semibold text-status-orange">
              Noch nicht berechnet {formatCurrency(paymentGap.notInvoicedAmount, paymentGap.currency)}
            </span>
          ) : null}
          {!notInvoiced && hasDraftInvoice ? (
            <span className="rounded-full bg-status-amber/15 px-2.5 py-0.5 text-xs font-semibold text-status-amber">
              Entwurf
              {draftAmount > 0
                ? ` ${formatCurrency(booking.draft_invoice_amount!, booking.currency)}`
                : ""}
            </span>
          ) : null}
          {!notInvoiced && !hasUnpaid && !hasDraftInvoice && hasNoInvoice ? (
            <span className="rounded-full bg-status-orange/15 px-2.5 py-0.5 text-xs font-semibold text-status-orange">
              Keine Rechnung
            </span>
          ) : null}
          {!notInvoiced &&
          !hasUnpaid &&
          !hasDraftInvoice &&
          !hasNoInvoice &&
          invoicesReady &&
          (booking.status === "checked_in" || booking.status === "confirmed") ? (
            <span className="rounded-full bg-status-green/15 px-2.5 py-0.5 text-xs font-medium text-status-green">
              {paymentGap?.stayInvoiced ? "Bezahlt" : "Keine offene Rechnung"}
            </span>
          ) : null}
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          <Section title="Gast">
            <InfoItem icon={<User className="h-4 w-4" />} label="Name" value={guestDisplayName} />
            <InfoItem
              icon={<User className="h-4 w-4" />}
              label="Gasttyp"
              value={guest ? guestTypeLabel(guest.guest_type) : null}
            />
            <InfoItem icon={<Phone className="h-4 w-4" />} label="Telefon" value={guest?.phone} />
            <InfoItem icon={<Mail className="h-4 w-4" />} label="E-Mail" value={guest?.email} />
            <InfoItem
              icon={<Calendar className="h-4 w-4" />}
              label="Geburtsdatum"
              value={guestBirthdate}
            />
            <InfoItem icon={<MapPin className="h-4 w-4" />} label="Adresse" value={guestAddress} />
          </Section>

          <Section title="Aufenthalt">
            <div className="flex gap-3">
              <span className="mt-0.5 shrink-0 text-label-tertiary">
                <MapPin className="h-4 w-4" />
              </span>
              <div className="min-w-0">
                <p className="text-xs font-medium uppercase tracking-wide text-label-tertiary">
                  Stellplatz
                </p>
                <p className="mt-0.5 text-sm text-label">{pitchLabel}</p>
                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                  <PitchPlanBadge booking={booking} />
                  <span className="text-xs text-label-secondary">
                    {pitchAssignmentLabel(booking.pitch_assignment_mode)}
                  </span>
                </div>
              </div>
            </div>
            <InfoItem icon={<Tag className="h-4 w-4" />} label="Platzkategorie" value={categoryLabel} />
            <InfoItem
              icon={<Calendar className="h-4 w-4" />}
              label="Zeitraum"
              value={`${formatIsoDateShort(booking.arrival_date)} → ${formatIsoDateShort(booking.departure_date)} (${booking.nights} N.)`}
            />
            <StayCalendarNotes
              arrivalDate={booking.arrival_date}
              departureDate={booking.departure_date}
            />
            <InfoItem
              icon={<Users className="h-4 w-4" />}
              label="Personen"
              value={partyParts.join(" · ")}
            />
            <InfoItem
              icon={<Car className="h-4 w-4" />}
              label="Anreiseart"
              value={arrivalType !== "—" ? arrivalType : null}
            />
            <InfoItem icon={<Plug className="h-4 w-4" />} label="Strom" value={electricityLabel} />
            <InfoItem
              icon={<Car className="h-4 w-4" />}
              label="Fahrzeug"
              value={
                licensePlate ||
                vehicleType ||
                (booking.vehicle_type ? booking.vehicle_type.replace(/_/g, " ") : null)
              }
            />
            {vehicleRows.map((row) => (
              <InfoItem
                key={row.label}
                icon={<Car className="h-4 w-4" />}
                label={row.label}
                value={row.value}
              />
            ))}
            <InfoItem
              icon={<Radio className="h-4 w-4" />}
              label="Transponder"
              value={booking.transponder_id}
            />
            <InfoItem
              icon={<KeyRound className="h-4 w-4" />}
              label="Schlüsselnummer"
              value={booking.key_number}
            />
            <InfoItem
              icon={<Receipt className="h-4 w-4" />}
              label="Gesamtbetrag"
              value={formatStayAmount(booking.total_amount, booking.currency, booking.price_pending)}
            />
            {paymentGap && paymentGap.paidAmount > 0 && notInvoiced ? (
              <InfoItem
                icon={<Receipt className="h-4 w-4" />}
                label="Bezahlt"
                value={formatCurrency(paymentGap.paidAmount, paymentGap.currency)}
              />
            ) : null}
            {notInvoiced && paymentGap ? (
              <InfoItem
                icon={<Receipt className="h-4 w-4" />}
                label="Noch nicht berechnet"
                value={
                  <span className="font-semibold text-status-orange">
                    {formatCurrency(paymentGap.notInvoicedAmount, paymentGap.currency)}
                  </span>
                }
              />
            ) : null}
            {bookingSource ? (
              <InfoItem icon={<Zap className="h-4 w-4" />} label="Quelle" value={bookingSource} />
            ) : null}
          </Section>
        </div>

        {addonLines.length > 0 ? (
          <section className="space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-label-secondary">
              Zusatzleistungen
            </h3>
            <ul className="space-y-1 rounded-xl border border-separator bg-fill-tertiary/30 p-4">
              {addonLines.map((line) => (
                <li key={line} className="text-sm text-label">
                  {line}
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {requestedFeatureNames.length > 0 ? (
          <section className="space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-label-secondary">
              Platzwünsche
            </h3>
            <p className="rounded-xl border border-separator bg-fill-tertiary/30 p-4 text-sm text-label">
              {requestedFeatureNames.join(", ")}
            </p>
          </section>
        ) : null}

        {booking.companions?.length ? (
          <section className="space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-label-secondary">
              Mitreisende
            </h3>
            <div className="rounded-xl border border-separator bg-fill-tertiary/30 p-4">
              <BookingCompanionsPanel
                companions={booking.companions}
                className="space-y-3"
                hideTitle
              />
            </div>
          </section>
        ) : null}

        {userGroups ? (
          <GuestStayInvoicesSection
            booking={booking}
            userGroups={userGroups}
            active={open}
            onInvoiceDraftOpened={setOpenedInvoiceId}
            onChanged={() => {
              reloadInvoices();
              onInvoiceChanged?.();
            }}
          />
        ) : null}

        {bookingMessage ? (
          <section className="space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-label-secondary">
              Nachricht
            </h3>
            <p className="rounded-xl border border-separator bg-fill-tertiary/30 p-4 text-sm text-label whitespace-pre-wrap">
              {bookingMessage}
            </p>
          </section>
        ) : null}

        {checkoutStrict && hasUnpaid && booking.status === "checked_in" ? (
          <p className="text-sm text-status-orange">
            Check-out ist gesperrt, solange Rechnungen offen sind.
          </p>
        ) : null}

        {hasDraftInvoice && booking.status === "checked_in" ? (
          <p className="text-sm text-status-amber">
            Die Rechnung wurde beim Check-in begonnen, aber noch nicht ausgestellt. Mit
            „Rechnung fortsetzen“ können Sie die Rechnungserstellung abschließen.
          </p>
        ) : null}

        {hasNoInvoice && booking.status === "checked_in" ? (
          <p className="text-sm text-status-orange">
            Für diesen Aufenthalt wurde noch keine Rechnung angelegt.
            {canCreateInvoice ? " Mit „Rechnung erstellen“ können Sie eine Rechnung anlegen." : ""}
          </p>
        ) : null}
      </div>
      </Modal>

      {userGroups ? (
        <>
          <OpenedInvoiceDraftModal
            invoiceId={openedInvoiceId}
            open={!!openedInvoiceId}
            userGroups={userGroups}
            onClose={() => setOpenedInvoiceId(null)}
            onChanged={() => {
              reloadInvoices();
              onInvoiceChanged?.();
            }}
          />
          <CreateBookingInvoiceModal
            open={createInvoiceOpen}
            booking={booking}
            userGroups={userGroups}
            onClose={() => setCreateInvoiceOpen(false)}
            onCreated={handleInvoiceCreated}
          />
        </>
      ) : null}
    </>
  );
}
